import { applies } from './conditions';
import { GLOBAL_PARAMS } from './globals';
import { formatValue, literalTokens, matchToken } from './params';
import { suggest } from './suggest';
import type { Engine, Param, Values } from './types';

export function engineParams(engine: Engine): Param[] {
  return [...engine.params, ...GLOBAL_PARAMS];
}

export type ModError =
  | { kind: 'unknown'; token: string; suggestions: string[] }
  | { kind: 'conflict'; token: string; param: string; previous: string }
  | { kind: 'inapplicable'; token: string; param: string; reason: string };

export interface ParsedMods {
  values: Values;
  errors: ModError[];
}

/** Maps modifier tokens onto an engine's params. Unknown or clashing tokens are reported, not dropped. */
export function parseMods(
  engine: Engine,
  mods: string[],
  base: Values = {}
): ParsedMods {
  const params = engineParams(engine);
  const values: Values = { ...base };
  const setBy: Record<string, string> = {};
  const errors: ModError[] = [];

  for (const token of mods) {
    let matched = false;
    for (const param of params) {
      const value = matchToken(param, token);
      if (value === undefined) continue;
      matched = true;
      const previous = setBy[param.id];
      if (previous !== undefined && previous !== token) {
        errors.push({ kind: 'conflict', token, param: param.label, previous });
      } else {
        values[param.id] = value;
        setBy[param.id] = token;
      }
      break;
    }
    if (!matched) {
      errors.push({
        kind: 'unknown',
        token,
        suggestions: suggest(token, params.flatMap(literalTokens)),
      });
    }
  }
  // Filters that don't apply alongside the others (e.g. video length with
  // image results) are errors too, so a typed bang never half-works.
  for (const param of params) {
    const token = setBy[param.id];
    if (!token) continue;
    const option =
      param.type === 'choice'
        ? param.options.find(o => o.value === values[param.id])
        : undefined;
    const limit = !applies(param, values)
      ? param
      : option && !applies(option, values)
        ? option
        : undefined;
    if (limit) {
      errors.push({
        kind: 'inapplicable',
        token,
        param: param.label,
        reason: limit.whenLabel ?? 'doesn’t apply with the other filters',
      });
    }
  }
  return { values, errors };
}

/** Canonical modifier tokens for a set of values, in the engine's param order. */
export function formatMods(engine: Engine, values: Values): string[] {
  const tokens: string[] = [];
  for (const param of engineParams(engine)) {
    const token = formatValue(param, values[param.id]);
    if (token) tokens.push(token);
  }
  return tokens;
}

export function describeError(e: ModError): string {
  if (e.kind === 'conflict')
    return `"${e.token}" conflicts with "${e.previous}" (both set ${e.param})`;
  if (e.kind === 'inapplicable') return `"${e.token}" (${e.param}) ${e.reason}`;
  const hint = e.suggestions.length
    ? ` — did you mean ${e.suggestions.map(s => `"${s}"`).join(' or ')}?`
    : '';
  return `Unknown modifier "${e.token}"${hint}`;
}
